import { useState } from "react";
import BackButton from "../../../../components/Button/BackButton";
import FormSelect from "../../../../components/Form/FormSelect";
import { getTimezonesWithOffset } from "../../../../utils/constants/timezones";
import { pdfTemplateOptions } from "../../../../utils/constants/pdfTemplates";
import { btn } from "../../../../components/Button/buttonStyles";

const timezones = getTimezonesWithOffset();
const templates = pdfTemplateOptions();

const toForm = (settings) => ({
  system_currency_id: settings?.system_currency_id ?? "",
  default_client_timezone: settings?.default_client_timezone ?? "",
  default_quote_template_id: settings?.default_quote_template_id ?? "",
  default_client_invoice_template_id:
    settings?.default_client_invoice_template_id ?? "",
});

const AccountSettingsForm = ({ settings, currencies, onSave, onCancel }) => {
  const [form, setForm] = useState(toForm(settings));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const current = {};
    if (!form.system_currency_id)
      current.system_currency_id = "System currency is required.";
    if (!form.default_client_timezone)
      current.default_client_timezone = "Time zone is required.";
    if (!form.default_quote_template_id)
      current.default_quote_template_id = "Quote PDF template is required.";
    if (!form.default_client_invoice_template_id)
      current.default_client_invoice_template_id =
        "Client invoice PDF template is required.";

    setErrors(current);
    if (Object.keys(current).length) return;

    setSaving(true);
    const ok = await onSave({
      system_currency_id: Number(form.system_currency_id),
      default_client_timezone: form.default_client_timezone,
      default_quote_template_id: Number(form.default_quote_template_id),
      default_client_invoice_template_id: Number(
        form.default_client_invoice_template_id
      ),
    });
    setSaving(false);
    if (ok) onCancel();
  };

  const currencyOptions = currencies.map((c) => ({
    value: c.id,
    label: `${c.code} - ${c.name}`,
  }));

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <BackButton onClick={onCancel} />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Edit Account Settings
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5 rounded-lg border border-gray-100 bg-white p-5 shadow-sm"
      >
        <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 md:grid-cols-2">
          <FormSelect
            label="System currency"
            name="system_currency_id"
            value={form.system_currency_id}
            onChange={handleChange}
            options={currencyOptions}
            error={errors.system_currency_id}
            required
          />
          <FormSelect
            label="Time zone for new clients"
            name="default_client_timezone"
            value={form.default_client_timezone}
            onChange={handleChange}
            options={timezones}
            error={errors.default_client_timezone}
            required
          />
          <FormSelect
            label="Default quote PDF template"
            name="default_quote_template_id"
            value={form.default_quote_template_id}
            onChange={handleChange}
            options={templates}
            error={errors.default_quote_template_id}
            required
          />
          <FormSelect
            label="Default client invoice PDF template"
            name="default_client_invoice_template_id"
            value={form.default_client_invoice_template_id}
            onChange={handleChange}
            options={templates}
            error={errors.default_client_invoice_template_id}
            required
          />
        </div>

        <div className="space-y-3 border-t border-gray-100 pt-3">
          <span className="block text-xs text-gray-500">
            Fields marked with <span className="text-danger-500">*</span> are mandatory.
          </span>
          <div className="flex gap-2 text-sm">
            <button
              type="submit"
              disabled={saving}
              className={btn("primary", "sm")}
            >
              {saving ? "Updating..." : "Update"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AccountSettingsForm;
